/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {h, Component} from 'preact';
import PropTypes from 'prop-types';
import {utils} from 'phenix-web-sdk';
import styles from '../styles/view.css';
import userDataStore from '../../shared/userDataStore';
import config from '../../config';

class Video extends Component {
  constructor(props) {
    super(props);

    this.video = null;
    this.videoContainer = null;
    this.renderer = null;
    this.played = false;
  }

  _getVideoTrack() {
    const mediaStream = this.props.stream;
    const stream = mediaStream ? mediaStream.getStream() : null;
    const tracks = stream ? stream.getVideoTracks() : [];

    return tracks.length > 0 ? tracks[0] : null;
  }

  _getAudioTrack() {
    const mediaStream = this.props.stream;
    const stream = mediaStream ? mediaStream.getStream() : null;
    const tracks = stream ? stream.getAudioTracks() : [];

    return tracks.length > 0 ? tracks[0] : null;
  }

  _getInitialMutedState() {
    if (this.mustStartMuted() || config.audio.muted || !this._getAudioTrack()) {
      return true;
    } else if (this.props.storeLocalStateKey && userDataStore.get('Audio:' + this.props.storeLocalStateKey, 'enabled') !== 'enabled') {
      return userDataStore.get('Audio:' + this.props.storeLocalStateKey, 'enabled') === 'disabled';
    } else if (typeof this.props.defaultAudioEnabled === 'boolean') {
      return !this.props.defaultAudioEnabled;
    }

    return false;
  }

  _getIntialVideoState() {
    if (typeof this.props.defaultVideoEnabled === 'boolean') {
      return this.props.defaultVideoEnabled;
    }

    return true;
  }

  getStream() {
    return this.props.stream;
  }

  getRenderer() {
    return this.renderer;
  }

  handlePlay() {
    const video = this.getVideo();

    if (video && !this.props.autoPlay && !this.played) {
      video.pause();
    }
  }

  mustStartMuted() {
    return (
      (config.environment.isMobile ||
        utils.rtc.browser === 'Safari' ||
        (utils.rtc.browser === 'Chrome' && utils.rtc.browserVersion >= 66)) &&
      this.isLiveOrRtmpStream()
    );
  }

  isLiveOrRtmpStream() {
    const mediaStream = this.props.stream;

    return mediaStream ? !mediaStream.getStream() : false;
  }

  shouldShowNativeControls() {
    return this.props.nativeControls || config.environment.isIOS || (config.environment.isSamsungBrowser && this.isLiveOrRtmpStream());
  }

  play() {
    if (!this.video) {
      return;
    }

    this.played = true;

    if (!this.renderer) {
      attachStreamToVideo.call(this, this.props.stream, this.video);
    } else {
      if (this.video.paused) {
        this.renderer.start(this.video);
      }
    }

    return !this.video.paused;
  }

  pause() {
    const video = this.getVideo();

    return video && video.pause();
  }

  isPaused() {
    const video = this.getVideo();

    return !video || !!video.paused;
  }

  getVideoState() {
    const videoTrack = this._getVideoTrack();

    return videoTrack ? videoTrack.enabled : true;
  }

  setVideoElement(videoElement) {
    this.video = videoElement;

    if (videoElement && videoElement.parentNode) {
      this.videoContainer = videoElement.parentNode;
    }
  }

  setVideoState(videoState) {
    const videoTrack = this._getVideoTrack();

    if (videoTrack) {
      videoTrack.enabled = !!videoState;
    }
  }

  getAudioMuted() {
    const video = this.getVideo();

    return !video || !!video.muted;
  }

  setAudioMuted(muted) {
    const audioTrack = this._getAudioTrack();

    if (audioTrack) {
      audioTrack.enabled = !muted;
    }

    this.getVideo().muted = muted;
  }

  rememberAudioState() {
    if (this.props.storeLocalStateKey) {
      userDataStore.put('Audio:' + this.props.storeLocalStateKey, this.getAudioMuted() ? 'disabled' : 'enabled');
    }
  }

  getVolume() {
    const video = this.getVideo();

    if (!video) {
      return 0;
    }

    return video.volume;
  }

  setVolume(volume) {
    const video = this.getVideo();

    if (!video) {
      return volume;
    }

    return (video.volume = volume);
  }

  requestFullscreen() {
    if (!isFullscreen()) {
      return adjustElementSizeAndRequestFullScreen.call(this, this.video);
    }
  }

  addListenerForExitFullscreen(listener) {
    addListenerForExitFullScreen.call(this, this.video, listener);
  }

  addEventListener(...args) {
    if (this.getVideo().addEventListener) {
      this.getVideo().addEventListener(...args);
    }
  }

  removeEventListener(...args) {
    if (this.getVideo().removeEventListener) {
      this.getVideo().removeEventListener(...args);
    }
  }

  isFullscreen() {
    return isFullscreen();
  }

  exitFullscreen() {
    return exitFullscreen();
  }

  getCurrentTime() {
    const video = this.getVideo();

    if (!video) {
      return 0;
    }

    return video.currentTime;
  }

  getBufferedStart() {
    const video = this.getVideo();

    if (!video || !video.buffered || video.buffered.length === 0) {
      return 0;
    }

    return parseInt(video.buffered.start(0));
  }

  getBufferedEnd() {
    const video = this.getVideo();

    if (!video.buffered || video.buffered.length === 0) {
      return 0;
    }

    return parseInt(video.buffered.end(video.buffered.length - 1));
  }

  seekTo(timestamp) {
    const video = this.getVideo();

    if (!video) {
      return;
    }

    video.currentTime = timestamp;
  }

  getContainer() {
    return this.videoContainer;
  }

  getVideo() {
    if (!this.videoContainer) {
      return this.video;
    }

    return (
      this.videoContainer.querySelector('video') ||
      this.videoContainer.querySelector('embed') ||
      this.videoContainer.querySelector('object')
    );
  }

  canAudioStateBeChanged() {
    return this.isLiveOrRtmpStream() || !!this._getAudioTrack();
  }

  canVideoStateBeChanged() {
    if (this.isLiveOrRtmpStream()) {
      return false;
    }

    return !!this._getVideoTrack();
  }

  componentDidMount() {
    this.setVideoState(this._getIntialVideoState());
    this.setAudioMuted(this._getInitialMutedState());

    if (!this.video) {
      console.warn('No video element.');

      return;
    }

    const parentNode = (this.videoContainer = this.video.parentNode);

    if (this.props.autoPlay) {
      // 2019-03-20 SBI Safari pauses the video if it's played in the same instant as attached to DOM
      setTimeout(() => {
        this.play();
      }, 1);
    } else {
      this.pause();
    }

    if (this.isLiveOrRtmpStream() && detectFlashPlugin()) {
      const embedOrObject = parentNode.querySelector('embed') || parentNode.querySelector('object');

      if (embedOrObject) {
        this.setVideoElement(embedOrObject);
      }
    }
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.stream !== this.props.stream;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.renderer) {
      prevProps.renderer.stop();
    }

    attachStreamToVideo.call(this, this.props.stream, this.video);
  }

  componentWillUnmount() {
    if (this.renderer) {
      this.renderer.stop();
    }

    if (this.props.onStreamEnd) {
      this.props.onStreamEnd(this.props.stream);
    }

    this.video = null;
    this.videoContainer = null;
    this.renderer = null;
    this.played = false;
  }

  render() {
    const {
      sessionId,
      id,
      stream,
      onStreamEnd,
      mediaStream,
      className = '',
      autoPlay,
      playsInline,
      height,
      width,
      onVolumeChange
    } = this.props;

    if (mediaStream) {
      const mediaStreamTrack = mediaStream.getTracks()[0];
      mediaStreamTrack.onended = () => {
        if (onStreamEnd) {
          onStreamEnd();
        }
      };
    }

    if (stream === undefined) {
      return <div className={styles.video}></div>;
    }

    return (
      <video
        ref={(element) => this.setVideoElement(element)}
        className={`${className} ${styles.video}`}
        height={this.isLiveOrRtmpStream() && detectFlashPlugin() ? 1080 : height}
        width={this.isLiveOrRtmpStream() && detectFlashPlugin() ? 1920 : width}
        onPlay={() => this.handlePlay()}
        onDoubleClick={({target}) => adjustElementSizeAndRequestFullScreen(target)}
        onVolumeChange={onVolumeChange}
        muted={this._getInitialMutedState()}
        controls={this.shouldShowNativeControls()}
        data-session-id={sessionId}
        data-stream-id={id}
        autoPlay={autoPlay === true}
        playsInline={playsInline === true}
      ></video>
    );
  }
}

Video.propTypes = {
  sessionId: PropTypes.string,
  id: PropTypes.string.isRequired,
  mediaStream: PropTypes.object,
  stream: PropTypes.object,
  className: PropTypes.string,
  playsInline: PropTypes.bool,
  autoPlay: PropTypes.bool,
  height: PropTypes.number,
  width: PropTypes.number,
  nativeControls: PropTypes.bool,
  defaultAudioEnabled: PropTypes.bool,
  defaultVideoEnabled: PropTypes.bool,
  storeLocalStateKey: PropTypes.string,

  renderer: PropTypes.object,

  onStreamEnd: PropTypes.func,
  onVolumeChange: PropTypes.func
};

function attachStreamToVideo(stream, video) {
  if (!stream) {
    return;
  }

  if (!video) {
    return console.warn('Video element not found. Unable to attach stream.');
  }

  if (stream.createRenderer) {
    this.renderer = stream.createRenderer();
    this.renderer.on('autoMuted', () => {
      this.setAudioMuted(true);
    });
    this.renderer.on('failedToPlay', () => {
      this.pause();
    });
    this.renderer.start(video);
  } else {
    utils.rtc.attachMediaStream(video, stream);
  }

  return video.src;
}

function adjustElementSizeAndRequestFullScreen(element) {
  this.previousElementHeight = element.style.height;
  this.previousElementWidth = element.style.width;
  this.previousParentElementHeight = this.videoContainer.style.height;
  this.previousParentElementWidth = this.videoContainer.style.width;

  element.style.width = '100%';
  element.style.height = '100%';
  this.videoContainer.style.width = '100%';
  this.videoContainer.style.height = '100%';

  if (isFullscreen()) {
    exitFullscreen();
  } else {
    if (utils.rtc.browser === 'IE' && utils.rtc.browserVersion < 11) {
      requestFullScreenForElement(element);
    } else {
      requestFullScreenForElement(this.videoContainer);
    }
  }

  addListenerForExitFullScreen.call(this, element);
}

function isFullscreen() {
  return (
    (document.fullscreenElement && document.fullscreenElement !== null) ||
    (document.webkitFullscreenElement && document.webkitFullscreenElement !== null) ||
    (document.mozFullScreenElement && document.mozFullScreenElement !== null) ||
    (document.msFullscreenElement && document.msFullscreenElement !== null)
  );
}

function requestFullScreenForElement(element) {
  if (!element) {
    throw new Error('Element required to request full screen');
  }

  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.mozRequestFullScreen) {
    element.mozRequestFullScreen();
  } else if (element.webkitRequestFullScreen) {
    element.webkitRequestFullScreen();
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen();
  }
}

function exitFullscreen() {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  } else if (document.mozCancelFullScreen) {
    document.mozCancelFullScreen();
  } else if (document.msExitFullscreen) {
    document.msExitFullscreen();
  }
}

function addListenerForExitFullScreen(element, callback) {
  const exitHandler = () => {
    if (!document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement && !document.fullscreenElement) {
      element.style.width = this.previousElementWidth || '';
      element.style.height = this.previousElementHeight || '';
      this.videoContainer.style.width = this.previousParentElementWidth || '';
      this.videoContainer.style.height = this.previousParentElementHeight || '';

      document.removeEventListener('fullscreenchange', exitHandler);
      document.removeEventListener('webkitfullscreenchange', exitHandler);
      document.removeEventListener('mozfullscreenchange', exitHandler);
      document.removeEventListener('MSFullscreenChange', exitHandler);

      if (callback) {
        return callback();
      }
    }
  };

  document.addEventListener('fullscreenchange', exitHandler, false);
  document.addEventListener('webkitfullscreenchange', exitHandler, false);
  document.addEventListener('mozfullscreenchange', exitHandler, false);
  document.addEventListener('MSFullscreenChange', exitHandler, false);
}

function detectFlashPlugin() {
  const defaultVersion = [10, 0, 0];
  const pluginName = 'Shockwave Flash';
  const mimeType = 'application/x-shockwave-flash';
  const activeX = 'ShockwaveFlash.ShockwaveFlash';
  let version = [0, 0, 0];

  // Firefox, Webkit, Opera
  if (utils.rtc.global.navigator && utils.rtc.global.navigator.plugins && utils.rtc.global.navigator.plugins[pluginName]) {
    const description = utils.rtc.global.navigator.plugins[pluginName].description;

    if (
      description &&
      utils.rtc.global.navigator &&
      utils.rtc.global.navigator.mimeTypes &&
      utils.rtc.global.navigator.mimeTypes[mimeType] &&
      utils.rtc.global.navigator.mimeTypes[mimeType].enabledPlugin
    ) {
      version = description.replace(pluginName, '').replace(/^\s+/, '').replace(/\sr/gi, '.').split('.');

      for (let i = 0, total = version.length; i < total; i++) {
        version[i] = parseInt(version[i].match(/\d+/), 10);
      }
    }
    // Internet Explorer / ActiveX
  } else if (utils.rtc.global.ActiveXObject) {
    try {
      const ax = new utils.rtc.global.ActiveXObject(activeX);

      if (ax) {
        const versionString = ax.GetVariable('$version') || '';
        const versionInfo = versionString.split(' ')[1].split(',');

        version = [parseInt(versionInfo[0], 10), parseInt(versionInfo[1], 10), parseInt(versionInfo[2], 10)];
      }
    } catch (e) {
      console.error(e);
    }
  }

  return (
    version[0] > defaultVersion[0] ||
    (version[0] === defaultVersion[0] && version[1] > 0) ||
    (version[0] === defaultVersion[0] && version[1] === 0 && version[2] >= 0)
  );
}

export default Video;